import React, { useEffect } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import { trackPageView } from '../../services/analytics';

interface ImageModalProps {
  images: string[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({
  images,
  currentIndex,
  isOpen,
  onClose,
  onPrevious,
  onNext,
}) => {
  useEffect(() => {
    if (isOpen) {
      trackPageView('/modal/image-gallery', 'Image Gallery');
    }
  }, [isOpen]);
  // ESCキーでモーダルを閉じる
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onPrevious();
          break;
        case 'ArrowRight':
          onNext();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onPrevious, onNext]);

  // モーダルが開いているときはスクロールを無効化
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <div
      data-testid="image-modal-root"
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      {/* オーバーレイ */}
      <div className="absolute inset-0 bg-black bg-opacity-90" />

      {/* メインコンテンツエリア */}
      <div className="relative z-10 flex flex-col items-center max-w-7xl max-h-full mx-4">
        {/* 画像コンテナ */}
        <div className="relative flex items-center justify-center mb-4">
          {/* 前の画像ボタン */}
          {images.length > 1 && (
            <button
              onClick={onPrevious}
              disabled={currentIndex === 0}
              aria-label="前の画像"
              className="absolute -left-16 p-3 bg-black bg-opacity-60 text-white rounded-full hover:bg-opacity-80 transition-all z-30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaChevronLeft size={24} />
            </button>
          )}

          {/* 画像 */}
          <img
            src={currentImage}
            alt={`画像 ${currentIndex + 1}`}
            className="max-w-full max-h-[80vh] object-contain"
            onError={e => {
              const target = e.target as HTMLImageElement;
              target.src = '/images/article-placeholder.svg';
            }}
          />

          {/* 次の画像ボタン */}
          {images.length > 1 && (
            <button
              onClick={onNext}
              disabled={currentIndex === images.length - 1}
              aria-label="次の画像"
              className="absolute -right-16 p-3 bg-black bg-opacity-60 text-white rounded-full hover:bg-opacity-80 transition-all z-30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaChevronRight size={24} />
            </button>
          )}

          {/* 閉じるボタン */}
          <button
            onClick={onClose}
            aria-label="モーダルを閉じる"
            className="absolute -top-12 -right-2 p-2 bg-black bg-opacity-60 text-white rounded-full hover:bg-opacity-80 transition-all z-30"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* 画像カウンター */}
        {images.length > 1 && (
          <div className="px-4 py-2 bg-black bg-opacity-60 text-white rounded-full text-sm font-medium">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageModal;
